import React, { useEffect, useState, useCallback } from 'react';
import api from '../api/axios';
import { useToast } from '../context/ToastContext';

const CustomersPage = () => {
  const [apartments, setApartments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Drill-down state
  const [selectedApartment, setSelectedApartment] = useState(null);
  const [apartmentData, setApartmentData] = useState(null); // includes customers
  const [loadingApt, setLoadingApt] = useState(false);
  
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const toast = useToast();

  const fetchApartments = useCallback(async () => {
    try {
      const { data } = await api.get('/apartments');
      setApartments(data.data);
    } catch {
      toast('Failed to load apartments', 'error');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetchApartments(); }, [fetchApartments]);

  const loadApartmentDetails = async (apt) => {
    setSelectedApartment(apt);
    setSelectedCustomer(null);
    setLoadingApt(true);
    try {
      const { data } = await api.get(`/apartments/${apt.id}`);
      setApartmentData(data.data);
    } catch {
      toast('Failed to load apartment details', 'error');
    } finally {
      setLoadingApt(false);
    }
  };

  const handleBackToApartments = () => {
    setSelectedApartment(null);
    setApartmentData(null);
    setSelectedCustomer(null);
  };

  const handleBackToCustomers = () => {
    setSelectedCustomer(null);
  };

  if (loading) return <div className="flex-center" style={{ height: 300 }}><div className="spinner"></div></div>;

  return (
    <div>
      <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700 }}>👤 Customer Details</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>
            {!selectedApartment ? 'Select an apartment to view customers' :
             !selectedCustomer ? `Customers in ${selectedApartment.apartment_name}` :
             `Details for ${selectedCustomer.customerName}`
            }
          </p>
        </div>
      </div>

      {/* Level 1: Apartments List */}
      {!selectedApartment && (
        <div className="apt-grid">
          {apartments.length === 0 ? (
            <div className="empty-state" style={{ gridColumn: '1 / -1' }}>
              <div className="empty-icon">🏢</div>
              <p>No apartments found.</p>
            </div>
          ) : apartments.map(apt => (
            <div key={apt.id} className="apt-card" style={{ cursor: 'pointer' }} onClick={() => loadApartmentDetails(apt)}>
              <div className="apt-name">{apt.apartment_name}</div>
              <div className="apt-location">📍 {apt.location || apt.address || 'No location set'}</div>
              <div className="apt-stats">
                <div className="apt-stat">
                  <div className="apt-stat-value">{apt.total_units || 0}</div>
                  <div className="apt-stat-label">Units</div>
                </div>
              </div>
              <button className="btn btn-secondary btn-sm" style={{ width: '100%', marginTop: 14, justifyContent: 'center' }}>
                View Customers →
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Level 2: Customers in Apartment */}
      {selectedApartment && !selectedCustomer && (
        <div>
          <button className="btn btn-secondary btn-sm" onClick={handleBackToApartments} style={{ marginBottom: 16 }}>
            ← Back to Apartments
          </button>
          
          {loadingApt ? (
            <div className="flex-center" style={{ height: 200 }}><div className="spinner"></div></div>
          ) : (
            <div className="dashboard-grid" style={{ gridTemplateColumns: '1fr' }}>
              <div className="dash-card">
                <div className="dash-card-header">
                  <div className="dash-card-title">Customers ({apartmentData?.customers?.length || 0})</div>
                </div>
                {apartmentData?.customers?.length === 0 ? (
                  <div className="empty-state" style={{ padding: '20px 0' }}>No customers found in this apartment.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Phone</th>
                          <th>Flat / Address</th>
                          <th>Total Orders</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {apartmentData?.customers?.map(c => (
                          <tr key={c.phone} style={{ cursor: 'pointer' }} onClick={() => setSelectedCustomer(c)}>
                            <td style={{ fontWeight: 600 }}>{c.customerName || 'Unknown'}</td>
                            <td>{c.phone}</td>
                            <td>{c.address}</td>
                            <td><span className="badge badge-MGR">{c.orders.length}</span></td>
                            <td>
                              <button className="btn btn-primary btn-sm">View Details</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Level 3: Customer Details */}
      {selectedCustomer && (
        <div>
          <button className="btn btn-secondary btn-sm" onClick={handleBackToCustomers} style={{ marginBottom: 16 }}>
            ← Back to Customers
          </button>
          
          <div className="dashboard-grid">
            <div className="dash-card">
              <div className="dash-card-header">
                <div className="dash-card-title">Customer Profile</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
                <div>
                  <div style={{ fontSize: 13, color: 'var(--text-3)', fontWeight: 600 }}>Name</div>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>{selectedCustomer.customerName}</div>
                </div>
                <div>
                  <div style={{ fontSize: 13, color: 'var(--text-3)', fontWeight: 600 }}>Phone</div>
                  <div style={{ fontSize: 16 }}>📞 {selectedCustomer.phone}</div>
                </div>
                <div>
                  <div style={{ fontSize: 13, color: 'var(--text-3)', fontWeight: 600 }}>Apartment & Address</div>
                  <div style={{ fontSize: 15 }}>🏢 {selectedApartment.apartment_name}</div>
                  <div style={{ fontSize: 15, color: 'var(--text-2)' }}>{selectedCustomer.address}</div>
                </div>
                <div>
                  <div style={{ fontSize: 13, color: 'var(--text-3)', fontWeight: 600 }}>Total Orders</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--primary)' }}>{selectedCustomer.orders.length} Orders</div>
                </div>
              </div>
            </div>

            <div className="dash-card" style={{ gridColumn: 'span 2' }}>
              <div className="dash-card-header">
                <div className="dash-card-title">Order History</div>
              </div>
              <div className="table-responsive" style={{ marginTop: 12 }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th>Date</th>
                      <th>Items</th>
                      <th>Total</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedCustomer.orders.map(order => (
                      <tr key={order.id}>
                        <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>#{order.id.slice(0,8).toUpperCase()}</td>
                        <td>{new Date(order.created_at).toLocaleDateString()}</td>
                        <td>
                          {order.items?.map(i => `${i.name} (x${i.quantity})`).join(', ')}
                        </td>
                        <td style={{ fontWeight: 600 }}>₹{parseFloat(order.total_amount).toFixed(2)}</td>
                        <td>
                          <span className={`badge badge-${order.order_status === 'delivered' ? 'success' : order.order_status === 'cancelled' ? 'danger' : 'warning'}`}>
                            {order.order_status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomersPage;
